import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { SkipAuthInterceptorHeader } from '@asamco/utils';
import { Observable, from, of, switchMap } from 'rxjs';

import { AsamcoAuthService } from '../auth/asamco-auth.service';
import { ENVIRONMENT } from '../configuration/appsettings';
import { BackEndApiType } from '../configuration/backend-api-type';
import { IAppConfig } from '../configuration/iappconfig.interface';
import { getServerSettingForRequest } from './get-server-setting-for-request';

/**
 * Interceptor that will add the authentication header
 */
@Injectable({
	providedIn: 'root',
	deps: [AsamcoAuthService, ENVIRONMENT],
})
export class AuthInterceptorService implements HttpInterceptor {
	private neverUpgradePartials = [
		this.environment.AsamcoAppSettings.serverSettings[BackEndApiType.Evolution]?.loginUrl,
		this.environment.AsamcoAppSettings.serverSettings[BackEndApiType.WebApi]?.loginUrl,
		this.environment.AsamcoAppSettings.serverSettings[BackEndApiType.WebApi]?.dbListUrl,
	].filter(partial => partial !== undefined);

	constructor(private authService: AsamcoAuthService, @Inject(ENVIRONMENT) private environment: { AsamcoAppSettings: IAppConfig }) {}

	intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		if (this.neverUpgradePartials.some(partial => request.url.includes(partial))) {
			// as it turns out, the asamco backends sometimes fail when there is an auth header on an
			// endpoint that doesn't require auth. made a way to blacklist those endpoints.
			return next.handle(request);
		}

		if (request.headers.has(SkipAuthInterceptorHeader)) {
			request = request.clone({
				headers: request.headers.delete(SkipAuthInterceptorHeader),
			});
			return next.handle(request);
		} else {
			const serverSettings = getServerSettingForRequest(this.authService, request);

			if (serverSettings) {
				// A bit ugly but we need to use the oidc service in case of the primary backend...
				if (this.authService.userSession && serverSettings === this.authService.userSession.backend) {
					return from(this.authService.getPrimaryTokenAsync()).pipe(
						switchMap(pt => this.requestFromToken(pt, serverSettings, request)),
						switchMap(req => next.handle(req))
					);
				} else {
					return this.requestFromToken(serverSettings.token, serverSettings, request).pipe(
						switchMap(req => next.handle(req))
					);
				}
			}
			return next.handle(request);
		}
	}

	requestFromToken(token: string, serverSettings, request) {
		// there is a config for this server, use it to add the needed header!
		const [header, ...prefix] = serverSettings.header.split(':');

		const newRequest = request.clone({
			headers: request.headers.set(header, `${prefix.join('')} ${token}`),
		});
		return of(newRequest);
	}
}
